'use client';

import { useSyncExternalStore } from 'react';

/**
 * The form-factor substrate (docs/MOBILE.md §5): the shared breakpoint constants
 * and a `matchMedia`-backed hook over them.
 *
 * The rendering rule: **structure switches in CSS, behaviour switches in JS.**
 * Chrome that differs per form factor (sidebar vs dock) renders both branches and
 * lets `@media` gate them, because the server render cannot know the viewport;
 * this hook is for *behavioural* decisions only (focus handling, gesture wiring,
 * which overlay variant a component opens), where the deterministic desktop
 * server snapshot is a harmless one-frame default.
 *
 * CSS custom properties cannot appear in an `@media` condition, so the pixel
 * values below are the single documented source and every `@media` in a CSS
 * Module mirrors them as literals with a comment pointing here.
 */

/** phone: <= 767px ("compact"); the v1 mobile target. */
export const PHONE_MAX_PX = 767;
/** small phone: <= 639px ("narrow"); a tweak-tier inside compact, use sparingly. */
export const SMALL_PHONE_MAX_PX = 639;
/** tablet: 768-1023px; v1 treats it as narrow-desktop. */
export const TABLET_MAX_PX = 1023;
/** wide desktop: >= 1180px, the width at which a page can afford a SECOND
 *  working column beside its content (the checklist's detail aside). Below it
 *  the page is one column and the aside does not exist. */
export const WIDE_MIN_PX = 1180;

export const COMPACT_QUERY = `(max-width: ${PHONE_MAX_PX}px)`;
export const NARROW_QUERY = `(max-width: ${SMALL_PHONE_MAX_PX}px)`;

export const WIDE_QUERY = `(min-width: ${WIDE_MIN_PX}px)`;

/** Coarse pointer (touch): the 44px-target and no-hover media features pair with this. */
export const COARSE_POINTER_QUERY = '(pointer: coarse)';

// One stable subscribe function per query, so useSyncExternalStore does not
// tear down and re-add the matchMedia listener on every render.
const subscribers = new Map<string, (onChange: () => void) => () => void>();
function subscribeTo(query: string): (onChange: () => void) => () => void {
  let sub = subscribers.get(query);
  if (!sub) {
    sub = (onChange) => {
      const mq = window.matchMedia(query);
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    };
    subscribers.set(query, sub);
  }
  return sub;
}

function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    subscribeTo(query),
    () => window.matchMedia(query).matches,
    // Server snapshot: deterministic desktop-first default (see the rendering rule).
    () => false,
  );
}

export interface ViewportFlags {
  /** Phone form factor (<= 767px): dock chrome, sheets, full-screen push. */
  compact: boolean;
  /** Small phone (<= 639px): implies `compact`. */
  narrow: boolean;
  /** Wide desktop (>= 1180px): a second working column fits beside the content. */
  wide: boolean;
}

/**
 * Live `compact` / `narrow` flags for behavioural form-factor decisions, as a
 * hook. Sits beside `useTheme` and
 * `usePopover` and follows the same external-store pattern.
 */
export function useViewport(): ViewportFlags {
  const compact = useMediaQuery(COMPACT_QUERY);
  const narrow = useMediaQuery(NARROW_QUERY);
  const wide = useMediaQuery(WIDE_QUERY);
  return { compact, narrow, wide };
}
